<script setup lang="ts">
import { Link } from "@inertiajs/vue3"
import { computed } from "vue"

import { formatDate, pluralize } from "@/lib/format"
import { ICON } from "@/lib/icons"
import { batchPath } from "@/routes"
import type { CaptureBatchSummary } from "@/types/capture"

const props = defineProps<{
  batch: CaptureBatchSummary
}>()

const statusLabel = computed(
  () => props.batch.status.charAt(0).toUpperCase() + props.batch.status.slice(1),
)
</script>

<template>
  <Link
    :href="batchPath({ id: batch.id })"
    data-testid="batch-card"
    class="border-default hover:bg-elevated/50 focus-visible:ring-primary block rounded-lg border p-4 transition focus-visible:ring-2 focus-visible:outline-none"
  >
    <div class="flex items-start gap-3">
      <UIcon :name="ICON.batches" class="text-primary mt-0.5 size-5 shrink-0" />
      <div class="min-w-0 flex-1 space-y-2">
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <h2 class="text-highlighted truncate font-semibold">{{ batch.name }}</h2>
            <p class="text-muted text-xs">
              {{ formatDate(batch.created_at) }} · Updated {{ formatDate(batch.updated_at) }}
            </p>
          </div>
          <UBadge color="neutral" variant="subtle">{{ statusLabel }}</UBadge>
        </div>

        <p v-if="batch.container" class="text-muted flex items-center gap-1.5 text-sm">
          <UIcon :name="ICON.container" class="size-4" />
          <span class="truncate">{{ batch.container.name }}</span>
        </p>

        <div class="text-muted flex flex-wrap gap-x-3 gap-y-1 text-xs">
          <span>{{ pluralize(batch.photo_count, "photo") }}</span>
          <span>{{ pluralize(batch.group_count, "group") }}</span>
          <span v-if="batch.unresolved_group_count > 0" class="text-warning">
            {{ batch.unresolved_group_count }} need review
          </span>
          <span v-else class="text-success">All reviewed</span>
        </div>
      </div>
      <UIcon :name="ICON.chevronRight" class="text-muted mt-1 size-4 shrink-0" />
    </div>
  </Link>
</template>
